import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { formatString } from '../../utils/StringUtils';

const ReverteDfdModal = ({ isOpen, dfd, onClose, onSuccess }) => {
  // Estados do componente
  const [justificativa, setJustificativa] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARACTERS = 500;

  // Efeito para resetar o estado quando o modal é reaberto
  useEffect(() => {
    if (isOpen) {
      setJustificativa('');
      setIsSubmitting(false);
      setError(null);
      setCharCount(0);
    }
  }, [isOpen]);

  // Atualiza contador de caracteres
  useEffect(() => {
    setCharCount(justificativa.length);
  }, [justificativa]);

  // Validação do formulário
  const validateForm = () => {
    if (!justificativa.trim()) {
      setError('Por favor, informe a justificativa para a reversão');
      return false;
    }

    if (justificativa.length > MAX_CHARACTERS) {
      setError(`A justificativa não pode exceder ${MAX_CHARACTERS} caracteres`);
      return false;
    }

    setError(null);
    return true;
  };

  // Submissão do formulário
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await onSuccess(justificativa);
      // O fechamento do modal é controlado pelo componente pai após sucesso
    } catch (err) {
      console.error('Erro ao reverter DFD:', err);
      setError(
        'Ocorreu um erro ao processar a reversão. Por favor, tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Manipulador de teclado
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      show={isOpen}
      // onHide={isSubmitting ? null : onClose}
      // backdrop={isSubmitting ? 'static' : true}
      // keyboard={!isSubmitting}
      onHide={onClose} //Mantém para o botão fechar funcionar
      backdrop="static" //Sempre estático - não fecha ao clicar fora
      keyboard={false} //Desabilita completamente o fechamento com ESC
      size="lg"
    >
      <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
        <h6 className="modal-title text-center m-0">
          <i className="bi bi-arrow-counterclockwise me-2"></i>
          Reverter {dfd?.tramitacao === 'APROVADO'
            ? 'Aprovação'
            : 'Reprovação'}{' '}
          do DFD {formatString(dfd?.num, 4)}
        </h6>
        <button
          className="btn btn-link text-white p-0 mb-0"
          onClick={onClose}
          title="Fechar"
          disabled={isSubmitting}
          style={{ fontSize: '0.9rem', textDecoration: 'none' }}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        <Form.Group controlId="formJustificativa">
          <Form.Label>
            Justificativa para Reversão ({charCount}/{MAX_CHARACTERS}{' '}
            caracteres)
          </Form.Label>

          <Form.Control
            as="textarea"
            rows={6}
            value={justificativa}
            onChange={e => setJustificativa(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Descreva detalhadamente o motivo para reverter ${
              dfd?.tramitacao === 'APROVADO' ? 'a aprovação' : 'a reprovação'
            } deste DFD...`}
            disabled={isSubmitting}
            className={error ? 'is-invalid' : ''}
            maxLength={MAX_CHARACTERS}
          />

          <Form.Text className="text-muted">
            Informe os motivos técnicos ou administrativos para reverter este
            DFD para "DEVOLVIDO PARA AJUSTES".
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="py-1">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          className="btn-sm py-1 px-3"
        >
          <i className="bi bi-x-lg me-1 fs-6"></i>
          Cancelar
        </Button>

        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !justificativa.trim()}
          className="btn-sm py-1 px-3"
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              Processando...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1 fs-6"></i>
              Confirmar Reversão
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// PropTypes para validação de props
ReverteDfdModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  dfd: PropTypes.shape({
    num: PropTypes.string,
    cnpj: PropTypes.string,
    tramitacao: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ReverteDfdModal;
